import { request } from '@/api/client'

/** Payouts radar (gateway ext/payoutRadar.ts): money-path violations between expected, collected and paid amounts. */
export type RadarRule =
  | 'paid_not_collected' | 'collected_mismatch' | 'partial_check' | 'overpaid' | 'underpaid' | 'double_paid' | 'returned_paid'
  | 'payout_mismatch' | 'handover_mismatch' | 'runner_short' | 'ptc_total_mismatch' | 'payout_orphan'
export type RadarSeverity = 'critical' | 'high' | 'medium' | 'low'
export type RadarStatus = 'open' | 'acknowledged' | 'resolved' | 'false_positive'

export interface RadarViolation {
  id: number
  key: string
  rule: RadarRule
  severity: RadarSeverity
  awb: string | null
  account: string
  runner: string
  expected: number | null
  collected: number | null
  paid: number | null
  diff: number
  ptcTrans: number[]
  apTrans: number[]
  payoutId: number | null
  detail: string
  eventAt: number | null
  status: RadarStatus
  owner: string | null
  notes: string | null
  firstSeen: number
  lastSeen: number
  resolvedAt: number | null
  resolvedBy: string | null
  autoClosed: boolean
}
export interface RadarSettings {
  enabled: boolean
  windowDays: number
  lookbackDays: number
  intervalMinutes: number
  /** HH:mm Cairo: one scan a day then; '' = every intervalMinutes. */
  dailyAt?: string
  tolerance: number
  refusalFeeCap: number
  rules: Record<RadarRule, boolean>
  notifyInApp: boolean
  notifyWhatsApp: boolean
  /** WhatsApp group jid for new violations ('' = the automatic payouts alert group). */
  alertGroup: string
  alertGroupName: string
  concurrency: number
}
export interface RadarScan {
  at: number; ms: number; from: string; to: string; by: string; calls: number; ptc: number; ap: number; apWindow: number; shipments: number; payouts: number
  found: number; created: number; reopened: number; closed: number; notified: { inApp: boolean; whatsapp: boolean | string }; error: string | null; byRule: Partial<Record<RadarRule, number>>
}
export interface RadarSummary {
  open: number
  newCount: number
  shortage: number
  overpaid: number
  doublePaid: number
  /** Open `overpaid` findings: the sum the clients were credited above what the runner handed in (added 2026-09-19; absent on older gateways). */
  clientOver?: number
  /** Open `underpaid` findings: the sum the clients are short, as a positive number. */
  clientShort?: number
  byRule: Partial<Record<RadarRule, number>>
  accounts: string[]
  lastScan: RadarScan | null
  scanning: boolean
  settings: RadarSettings
}
export interface RadarQuery { status?: string; rule?: string; severity?: string; account?: string; from?: string; to?: string; q?: string }

const base = '/ext/payout-radar'
export const payoutRadarService = {
  list: (q: RadarQuery, signal?: AbortSignal) => request<{ rows: RadarViolation[]; summary: RadarSummary }>(base, { query: q as Record<string, string>, signal }).then((r) => r.data),
  count: (signal?: AbortSignal) => request<{ open: number }>(`${base}/count`, { signal }).then((r) => r.data.open),
  scan: () => request<RadarScan>(`${base}/scan`, { method: 'POST', body: {} }).then((r) => r.data),
  act: (id: number, action: 'acknowledge' | 'resolve' | 'false_positive' | 'reopen' | 'note', note?: string) => request<RadarViolation>(`${base}/${id}/${action}`, { method: 'POST', body: { note } }).then((r) => r.data),
  saveSettings: (patch: Partial<RadarSettings>) => request<RadarSettings>(`${base}/settings`, { method: 'PUT', body: patch }).then((r) => r.data),
}
